import type { DateTime } from "luxon";
import { createContext, useContext } from "react";
import type { DayItem, HoursSummaryEmployeeDayShift, Nurse, NurseInShift, ShiftPerson, ShiftRequest, SpecificPreference, WeekData } from "@/utils/models";
import type { IsShiftSalaried } from "@/utils/wageUtils";

/** Where a day sits in the weeks the day items were built from. */
export type WeekOfDate = { week: WeekData; dayIndex: number };

export type WeekViewContextType = {
	/** Data-week-indexed (Sun-aligned). Safe to index with `getDayIndexByDate`. */
	weekViewData: DayItem[];
	/** Visible-position-indexed (rotated by `viewStartDay`). Renderer use only — do NOT index by date. */
	visibleWeekViewData: DayItem[];
	costlyShiftsByDay: Map<string, NurseInShift[]>;
	shiftHoursById: Map<string, HoursSummaryEmployeeDayShift>;
	actualShiftIds: Set<string>;
	recentCallInsCounts: Record<number, number>;
	getPtoCost: (date: DateTime, nurseId: number) => number | undefined;
	/** The requests showing on `date`, any date: a swap shows on its own day and on its target shift's day. */
	requestsOnDate: (date: DateTime) => ShiftRequest[];
	/** The weeks the day items were built from (the cache's with the flag on, the old list's off), and the shown week's position in them. */
	scheduleWeeks: WeekData[];
	scheduleWeekIndex: number;
	/** The people those weeks name, and one of them by id; with the flag on, no one until the roster loads. */
	scheduleNurses: Nurse[];
	getScheduleNurse: (id: number) => Nurse | undefined;
	/** What the grids show about the weeks' people, and one of them by id; with the flag on, there as soon as the weeks are. */
	schedulePeople: ShiftPerson[];
	getSchedulePerson: (id: number) => ShiftPerson | undefined;
	/** Everyone's time off and preferences by nurse id: with the cache on the schedule's, every row touching the weeks; off, the old list's. */
	timeOffByNurseId: Map<number, SpecificPreference[]>;
	/** Whether a shift's nurse is salaried on its date: off the shift with the flag on, from the roster's wages off. */
	isShiftSalaried: IsShiftSalaried;
	/** `date`'s week and its position in it, in `scheduleWeeks`; undefined when the day isn't in them. */
	weekOfDate: (date: DateTime) => WeekOfDate | undefined;
	/** The first and last day of the schedule; undefined while unknown. */
	firstDate: DateTime | undefined;
	lastDate: DateTime | undefined;
	/** The weeks around the shown one or the roster is loading, or failed to (with the flag on only); `refetch` asks again. */
	isLoading: boolean;
	isError: boolean;
	/** The shown week alone, which the Week grid waits for: it shows before the roster and the weeks either side. Failed when any week did. */
	isScheduleLoading: boolean;
	isScheduleError: boolean;
	refetch: () => void;
};

export const WeekViewContext = createContext<WeekViewContextType | null>(null);

/** The week's day items, or null outside the staffing pages, where nothing computes them. */
export const useOptionalWeekViewContext = (): WeekViewContextType | null => useContext(WeekViewContext);

export const useWeekViewContext = () => {
	const context = useContext(WeekViewContext);
	if (!context) throw new Error("useWeekViewContext must be used within a WeekViewContextProvider");
	return context;
};
